import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Save, Plus, Trash2, ChevronUp, ChevronDown, GripVertical, RotateCcw,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loading } from "@/components/shared";

export const BLOCK_LABELS = {
  h2: "Section name", h3: "Subheading", p: "Paragraph",
  ul: "Bulleted list", ol: "Numbered list", steps: "Numbered steps",
  callout: "Callout box", mistakes: "Common mistakes", groups: "Grouped list",
  defcards: "Definition cards", table: "Comparison table", numbered: "Numbered cards",
  scripts: "Script cards", product: "Tool promo card",
};
export const ADDABLE = ["h2", "h3", "p", "ul", "ol", "groups", "defcards", "table", "numbered", "scripts", "callout", "product"];

// ---- text <-> structured helpers ----
const ulToText = (items = []) =>
  items.map((it) => (typeof it === "string" ? it : [it.label, ...(it.sub || []).map((s) => "  " + s)].join("\n"))).join("\n");

const textToUl = (text) => {
  const out = [];
  text.split("\n").forEach((raw) => {
    if (!raw.trim() && !raw.startsWith("  ")) return;
    if (raw.startsWith("  ")) {
      const val = raw.trim();
      if (!val) return;
      let last = out[out.length - 1];
      if (typeof last === "string") { last = { label: last, sub: [] }; out[out.length - 1] = last; }
      if (!last) { out.push({ label: "", sub: [val] }); }
      else { last.sub = last.sub || []; last.sub.push(val); }
    } else if (raw.trim()) {
      out.push(raw.trim());
    }
  });
  return out;
};

const stepBlocksToText = (blocks = []) => {
  const lines = [];
  blocks.forEach((b) => {
    if (b.t === "p") lines.push(b.text);
    else if (b.t === "ul") (b.items || []).forEach((it) => {
      if (typeof it === "string") lines.push("- " + it);
      else { lines.push("- " + it.label); (it.sub || []).forEach((s) => lines.push("  - " + s)); }
    });
  });
  return lines.join("\n");
};

const textToStepBlocks = (text) => {
  const blocks = [];
  let curUl = null;
  text.split("\n").forEach((raw) => {
    const line = raw.trimEnd();
    if (!line.trim()) return;
    const isSub = /^\s{2,}-\s/.test(line);
    const isBullet = /^-\s/.test(line.trim());
    if (isSub) {
      if (curUl && curUl.items.length) {
        const val = line.trim().replace(/^-\s/, "");
        let last = curUl.items[curUl.items.length - 1];
        if (typeof last === "string") { last = { label: last, sub: [] }; curUl.items[curUl.items.length - 1] = last; }
        last.sub = last.sub || []; last.sub.push(val);
      }
    } else if (isBullet) {
      if (!curUl) { curUl = { t: "ul", items: [] }; blocks.push(curUl); }
      curUl.items.push(line.trim().replace(/^-\s/, ""));
    } else {
      curUl = null;
      blocks.push({ t: "p", text: line.trim() });
    }
  });
  return blocks;
};

export default function ChapterEditor() {
  const { slug } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user && !user.is_admin) return;
    api.get(`/admin/chapters/${slug}`).then((r) => setData(r.data)).catch(() => {});
  }, [slug, user]);

  if (user && !user.is_admin) return <div className="text-nm-neutral">Admin access required.</div>;
  if (!data) return <Loading label="Loading editor…" />;

  const setField = (k, v) => setData({ ...data, [k]: v });
  const setBlocks = (blocks) => setData({ ...data, blocks });
  const updateBlock = (i, patch) => setBlocks(data.blocks.map((b, idx) => (idx === i ? { ...b, ...patch } : b)));
  const moveBlock = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= data.blocks.length) return;
    const arr = [...data.blocks];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setBlocks(arr);
  };
  const deleteBlock = (i) => setBlocks(data.blocks.filter((_, idx) => idx !== i));
  const addBlock = (t) => {
    const nb = t === "ul" || t === "ol" ? { t, items: [] }
      : t === "steps" ? { t, items: [{ title: "", blocks: [] }] }
      : t === "groups" ? { t, items: [{ label: "", items: [] }] }
      : t === "defcards" ? { t, items: [{ term: "", desc: "" }] }
      : t === "numbered" ? { t, items: [{ title: "", lead: "", bullets: [] }] }
      : t === "scripts" ? { t, items: [{ label: "", text: "" }] }
      : t === "table" ? { t, headers: ["", ""], rows: [["", ""]] }
      : t === "product" ? { t, eyebrow: "Digital tool", title: "", price: "", desc: "", bullets: [], cta_label: "Explore the tool", cta_url: "", note: "" }
      : t === "callout" ? { t, tone: "start", title: "", text: "" }
      : t === "mistakes" ? { t, title: "Common mistakes", items: [] }
      : { t, text: "" };
    setBlocks([...data.blocks, nb]);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/chapters/${slug}`, {
        title: data.title, summary: data.summary, section_label: data.section_label,
        tool_preview: data.tool_preview, blocks: data.blocks,
        related_templates: data.related_templates || [],
      });
      toast.success("Chapter saved");
      nav(`/app/chapter/${slug}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const resetToDefault = async () => {
    if (!window.confirm("Discard all edits and restore the original built-in content?")) return;
    await api.post(`/admin/chapters/${slug}/reset`);
    toast.success("Restored original content");
    nav(`/app/chapter/${slug}`);
  };

  const ta = "w-full rounded-xl border border-nm-primary/15 bg-white px-3.5 py-2.5 text-sm text-nm-ink focus:outline-none focus:ring-2 focus:ring-nm-primary/40 transition-shadow";

  return (
    <div className="max-w-3xl pb-24" data-testid="chapter-editor">
      <div className="flex items-center justify-between gap-3">
        <Link to={`/app/chapter/${slug}`} className="inline-flex items-center gap-1.5 text-sm text-nm-neutral hover:text-nm-primary transition-colors" data-testid="editor-back">
          <ArrowLeft size={15} /> Back to chapter
        </Link>
        <div className="flex items-center gap-2">
          <button onClick={resetToDefault} data-testid="editor-reset" className="inline-flex items-center gap-1.5 rounded-full border border-nm-primary/15 px-4 py-2 text-sm text-nm-neutral hover:text-nm-ink transition-colors">
            <RotateCcw size={14} /> Restore original
          </button>
          <button onClick={save} disabled={saving} data-testid="editor-save" className="inline-flex items-center gap-2 rounded-full bg-nm-coral px-5 py-2 text-sm font-medium text-white hover:brightness-105 disabled:opacity-60 transition-[filter]">
            <Save size={15} /> {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>

      <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-nm-coral mt-6">Editing · {data.section_title}</div>
      <h1 className="font-serif text-3xl text-nm-ink mt-1">Chapter editor</h1>

      {/* Meta */}
      <div className="mt-6 space-y-4 rounded-2xl border border-nm-primary/12 bg-nm-surface p-6">
        <Field label="Section label"><input value={data.section_label} onChange={(e) => setField("section_label", e.target.value)} className={ta} data-testid="field-section-label" /></Field>
        <Field label="Chapter title"><input value={data.title} onChange={(e) => setField("title", e.target.value)} className={ta} data-testid="field-title" /></Field>
        <Field label="Framing summary"><textarea value={data.summary} onChange={(e) => setField("summary", e.target.value)} rows={2} className={ta} data-testid="field-summary" /></Field>
        <Field label="Related-tool note (right sidebar, optional)"><input value={data.tool_preview || ""} onChange={(e) => setField("tool_preview", e.target.value)} className={ta} data-testid="field-tool" /></Field>
        <Field label="Worksheets shown on this chapter">
          <div className="flex flex-wrap gap-2" data-testid="chapter-templates">
            {(data.all_templates || []).map((t) => {
              const on = (data.related_templates || []).includes(t.slug);
              return (
                <button key={t.slug} type="button" data-testid={`tpl-toggle-${t.slug}`}
                  onClick={() => setField("related_templates", on
                    ? (data.related_templates || []).filter((s) => s !== t.slug)
                    : [...(data.related_templates || []), t.slug])}
                  className={`rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors ${on ? "bg-nm-primary text-white border-nm-primary" : "border-nm-primary/20 text-nm-neutral hover:bg-nm-primary/5"}`}>
                  {on ? "✓ " : "+ "}{t.title}
                </button>
              );
            })}
          </div>
        </Field>
      </div>

      {/* Blocks */}
      <div className="mt-6 space-y-4">
        {data.blocks.map((blk, i) => (
          <div key={i} className="rounded-2xl border border-nm-primary/12 bg-nm-surface p-4" data-testid={`block-${i}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-nm-primary">
                <GripVertical size={15} className="text-nm-neutral/40" />
                <span className="text-xs font-semibold uppercase tracking-wide">{BLOCK_LABELS[blk.t] || blk.t}</span>
              </div>
              <div className="flex items-center gap-1">
                <IconBtn onClick={() => moveBlock(i, -1)} testid={`up-${i}`}><ChevronUp size={16} /></IconBtn>
                <IconBtn onClick={() => moveBlock(i, 1)} testid={`down-${i}`}><ChevronDown size={16} /></IconBtn>
                <IconBtn onClick={() => deleteBlock(i)} testid={`del-${i}`} danger><Trash2 size={15} /></IconBtn>
              </div>
            </div>
            <BlockEditor blk={blk} i={i} updateBlock={updateBlock} ta={ta} />
          </div>
        ))}
      </div>

      {/* Add block */}
      <div className="mt-6 rounded-2xl border border-dashed border-nm-primary/25 p-4">
        <div className="text-sm text-nm-neutral mb-3 flex items-center gap-2"><Plus size={15} /> Add a block</div>
        <div className="flex flex-wrap gap-2">
          {ADDABLE.map((t) => (
            <button key={t} onClick={() => addBlock(t)} data-testid={`add-${t}`}
              className="rounded-full border border-nm-primary/15 px-3.5 py-1.5 text-xs font-medium text-nm-primary hover:bg-nm-primary/5 transition-colors">
              {BLOCK_LABELS[t]}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs font-medium text-nm-neutral uppercase tracking-wide">{label}</label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

export function IconBtn({ onClick, children, danger, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`h-8 w-8 flex items-center justify-center rounded-lg transition-colors ${danger ? "text-nm-coral hover:bg-nm-coral/10" : "text-nm-neutral hover:bg-nm-primary/[0.06] hover:text-nm-ink"}`}>
      {children}
    </button>
  );
}

export function BlockEditor({ blk, i, updateBlock, ta }) {
  if (blk.t === "h2" || blk.t === "h3" || blk.t === "p") {
    return <textarea value={blk.text || ""} onChange={(e) => updateBlock(i, { text: e.target.value })} rows={blk.t === "p" ? 4 : 1} className={ta} placeholder="Text…" data-testid={`text-${i}`} />;
  }
  if (blk.t === "ul" || blk.t === "ol" || blk.t === "mistakes") {
    return (
      <div>
        {blk.t === "mistakes" && (
          <input value={blk.title || ""} onChange={(e) => updateBlock(i, { title: e.target.value })} className={`${ta} mb-2`} placeholder="Heading (e.g. Common mistakes)" />
        )}
        <textarea
          defaultValue={ulToText(blk.items)}
          onBlur={(e) => updateBlock(i, { items: textToUl(e.target.value) })}
          rows={Math.max(3, (ulToText(blk.items).split("\n").length))}
          className={ta}
          placeholder={blk.t === "ol" ? "One numbered item per line.\nIndent with 2 spaces for a sub-point." : "One bullet per line.\nIndent with 2 spaces for a sub-bullet."}
          data-testid={`list-${i}`}
        />
        <p className="text-[11px] text-nm-neutral/70 mt-1.5">
          {blk.t === "ol"
            ? "One item per line (auto-numbered). Indent with 2 spaces for a sub-point."
            : "One item per line. Indent with 2 spaces to make a sub-bullet."}
        </p>
      </div>
    );
  }
  if (blk.t === "callout") {
    return (
      <div className="space-y-2.5">
        <div className="flex gap-2">
          <select value={blk.tone || "start"} onChange={(e) => updateBlock(i, { tone: e.target.value })} className={`${ta} max-w-[180px]`} data-testid={`tone-${i}`}>
            <option value="start">Start-here (highlight)</option>
            <option value="simple">Simple approach</option>
            <option value="note">Neutral note</option>
          </select>
          <input value={blk.title || ""} onChange={(e) => updateBlock(i, { title: e.target.value })} className={ta} placeholder="Callout title" />
        </div>
        <textarea value={blk.text || ""} onChange={(e) => updateBlock(i, { text: e.target.value })} rows={3} className={ta} placeholder="Callout text (optional)" />
        <textarea defaultValue={ulToText(blk.items || [])} onBlur={(e) => { const v = e.target.value.trim(); updateBlock(i, { items: v ? textToUl(v) : undefined }); }} rows={2} className={ta} placeholder="Optional bullets — one per line" />
        <input value={blk.footer || ""} onChange={(e) => updateBlock(i, { footer: e.target.value })} className={ta} placeholder="Closing line (optional)" />
      </div>
    );
  }
  if (blk.t === "steps") {
    const items = blk.items || [];
    const setStep = (si, patch) => updateBlock(i, { items: items.map((s, idx) => (idx === si ? { ...s, ...patch } : s)) });
    const addStep = () => updateBlock(i, { items: [...items, { title: "", blocks: [] }] });
    const delStep = (si) => updateBlock(i, { items: items.filter((_, idx) => idx !== si) });
    const moveStep = (si, dir) => {
      const j = si + dir; if (j < 0 || j >= items.length) return;
      const arr = [...items]; [arr[si], arr[j]] = [arr[j], arr[si]]; updateBlock(i, { items: arr });
    };
    return (
      <div className="space-y-3">
        {items.map((s, si) => (
          <div key={si} className="rounded-xl border border-nm-primary/10 bg-nm-bg/50 p-3">
            <div className="flex items-center gap-2 mb-2">
              <span className="font-serif text-lg text-nm-primary/50 w-6">{si + 1}</span>
              <input value={s.title || ""} onChange={(e) => setStep(si, { title: e.target.value })} className={ta} placeholder="Step title" data-testid={`step-title-${i}-${si}`} />
              <IconBtn onClick={() => moveStep(si, -1)}><ChevronUp size={15} /></IconBtn>
              <IconBtn onClick={() => moveStep(si, 1)}><ChevronDown size={15} /></IconBtn>
              <IconBtn onClick={() => delStep(si)} danger><Trash2 size={14} /></IconBtn>
            </div>
            <textarea
              defaultValue={stepBlocksToText(s.blocks)}
              onBlur={(e) => setStep(si, { blocks: textToStepBlocks(e.target.value) })}
              rows={3} className={ta}
              placeholder={"Step details. Plain lines = paragraphs.\nLines starting with '- ' = bullets ('  - ' for sub-bullets)."}
            />
          </div>
        ))}
        <button onClick={addStep} className="inline-flex items-center gap-1.5 rounded-full border border-nm-primary/15 px-3.5 py-1.5 text-xs font-medium text-nm-primary hover:bg-nm-primary/5 transition-colors" data-testid={`add-step-${i}`}>
          <Plus size={13} /> Add step
        </button>
      </div>
    );
  }
  if (blk.t === "groups") {
    const groups = blk.items || [];
    const setGroup = (gi, patch) => updateBlock(i, { items: groups.map((g, idx) => (idx === gi ? { ...g, ...patch } : g)) });
    const addGroup = () => updateBlock(i, { items: [...groups, { label: "", items: [] }] });
    const delGroup = (gi) => updateBlock(i, { items: groups.filter((_, idx) => idx !== gi) });
    const moveGroup = (gi, dir) => {
      const j = gi + dir; if (j < 0 || j >= groups.length) return;
      const arr = [...groups]; [arr[gi], arr[j]] = [arr[j], arr[gi]]; updateBlock(i, { items: arr });
    };
    return (
      <div className="space-y-3">
        {groups.map((g, gi) => (
          <div key={gi} className="rounded-xl border border-nm-primary/10 bg-nm-bg/50 p-3">
            <div className="flex items-center gap-2 mb-2">
              <input value={g.label || ""} onChange={(e) => setGroup(gi, { label: e.target.value })} className={ta} placeholder="Group label (e.g. Leading people)" data-testid={`group-label-${i}-${gi}`} />
              <IconBtn onClick={() => moveGroup(gi, -1)}><ChevronUp size={15} /></IconBtn>
              <IconBtn onClick={() => moveGroup(gi, 1)}><ChevronDown size={15} /></IconBtn>
              <IconBtn onClick={() => delGroup(gi)} danger><Trash2 size={14} /></IconBtn>
            </div>
            <textarea
              defaultValue={(g.items || []).join("\n")}
              onBlur={(e) => setGroup(gi, { items: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })}
              rows={Math.max(2, (g.items || []).length)} className={ta}
              placeholder="One item per line"
              data-testid={`group-items-${i}-${gi}`}
            />
          </div>
        ))}
        <button onClick={addGroup} className="inline-flex items-center gap-1.5 rounded-full border border-nm-primary/15 px-3.5 py-1.5 text-xs font-medium text-nm-primary hover:bg-nm-primary/5 transition-colors" data-testid={`add-group-${i}`}>
          <Plus size={13} /> Add group
        </button>
      </div>
    );
  }
  if (blk.t === "defcards") {
    const items = blk.items || [];
    const set = (ci, patch) => updateBlock(i, { items: items.map((c, idx) => (idx === ci ? { ...c, ...patch } : c)) });
    const add = () => updateBlock(i, { items: [...items, { term: "", desc: "" }] });
    const del = (ci) => updateBlock(i, { items: items.filter((_, idx) => idx !== ci) });
    const move = (ci, dir) => { const j = ci + dir; if (j < 0 || j >= items.length) return; const arr = [...items]; [arr[ci], arr[j]] = [arr[j], arr[ci]]; updateBlock(i, { items: arr }); };
    return (
      <div className="space-y-3">
        {items.map((c, ci) => (
          <div key={ci} className="rounded-xl border border-nm-primary/10 bg-nm-bg/50 p-3">
            <div className="flex items-center gap-2 mb-2">
              <input value={c.term || ""} onChange={(e) => set(ci, { term: e.target.value })} className={ta} placeholder="Card title (e.g. Reliability)" data-testid={`defcard-term-${i}-${ci}`} />
              <IconBtn onClick={() => move(ci, -1)}><ChevronUp size={15} /></IconBtn>
              <IconBtn onClick={() => move(ci, 1)}><ChevronDown size={15} /></IconBtn>
              <IconBtn onClick={() => del(ci)} danger><Trash2 size={14} /></IconBtn>
            </div>
            <textarea value={c.desc || ""} onChange={(e) => set(ci, { desc: e.target.value })} rows={2} className={ta} placeholder="Description" data-testid={`defcard-desc-${i}-${ci}`} />
          </div>
        ))}
        <button onClick={add} className="inline-flex items-center gap-1.5 rounded-full border border-nm-primary/15 px-3.5 py-1.5 text-xs font-medium text-nm-primary hover:bg-nm-primary/5 transition-colors" data-testid={`add-defcard-${i}`}><Plus size={13} /> Add card</button>
      </div>
    );
  }
  if (blk.t === "scripts") {
    const items = blk.items || [];
    const set = (si, patch) => updateBlock(i, { items: items.map((c, idx) => (idx === si ? { ...c, ...patch } : c)) });
    const add = () => updateBlock(i, { items: [...items, { label: "", text: "" }] });
    const del = (si) => updateBlock(i, { items: items.filter((_, idx) => idx !== si) });
    const move = (si, dir) => { const j = si + dir; if (j < 0 || j >= items.length) return; const arr = [...items]; [arr[si], arr[j]] = [arr[j], arr[si]]; updateBlock(i, { items: arr }); };
    return (
      <div className="space-y-3">
        {items.map((c, si) => (
          <div key={si} className="rounded-xl border border-nm-primary/10 bg-nm-bg/50 p-3">
            <div className="flex items-center gap-2 mb-2">
              <input value={c.label || ""} onChange={(e) => set(si, { label: e.target.value })} className={ta} placeholder="Label (e.g. Recognition)" data-testid={`script-label-${i}-${si}`} />
              <IconBtn onClick={() => move(si, -1)}><ChevronUp size={15} /></IconBtn>
              <IconBtn onClick={() => move(si, 1)}><ChevronDown size={15} /></IconBtn>
              <IconBtn onClick={() => del(si)} danger><Trash2 size={14} /></IconBtn>
            </div>
            <textarea value={c.text || ""} onChange={(e) => set(si, { text: e.target.value })} rows={3} className={ta} placeholder="Script text. Wrap fill-in blanks in [brackets]." data-testid={`script-text-${i}-${si}`} />
          </div>
        ))}
        <button onClick={add} className="inline-flex items-center gap-1.5 rounded-full border border-nm-primary/15 px-3.5 py-1.5 text-xs font-medium text-nm-primary hover:bg-nm-primary/5 transition-colors" data-testid={`add-script-${i}`}><Plus size={13} /> Add script</button>
        <p className="text-[11px] text-nm-neutral/70">Text inside [brackets] shows as a highlighted fill-in placeholder.</p>
      </div>
    );
  }
  if (blk.t === "numbered") {
    const items = blk.items || [];
    const set = (ni, patch) => updateBlock(i, { items: items.map((c, idx) => (idx === ni ? { ...c, ...patch } : c)) });
    const add = () => updateBlock(i, { items: [...items, { title: "", lead: "", bullets: [] }] });
    const del = (ni) => updateBlock(i, { items: items.filter((_, idx) => idx !== ni) });
    const move = (ni, dir) => { const j = ni + dir; if (j < 0 || j >= items.length) return; const arr = [...items]; [arr[ni], arr[j]] = [arr[j], arr[ni]]; updateBlock(i, { items: arr }); };
    return (
      <div className="space-y-3">
        {items.map((c, ni) => (
          <div key={ni} className="rounded-xl border border-nm-primary/10 bg-nm-bg/50 p-3">
            <div className="flex items-center gap-2 mb-2">
              <span className="font-serif text-lg text-nm-primary/50 w-6">{ni + 1}</span>
              <input value={c.title || ""} onChange={(e) => set(ni, { title: e.target.value })} className={ta} placeholder="Title (e.g. Reliability)" data-testid={`num-title-${i}-${ni}`} />
              <IconBtn onClick={() => move(ni, -1)}><ChevronUp size={15} /></IconBtn>
              <IconBtn onClick={() => move(ni, 1)}><ChevronDown size={15} /></IconBtn>
              <IconBtn onClick={() => del(ni)} danger><Trash2 size={14} /></IconBtn>
            </div>
            <textarea value={c.lead || ""} onChange={(e) => set(ni, { lead: e.target.value })} rows={2} className={`${ta} mb-2`} placeholder="Lead sentence / question (optional)" />
            <input value={c.sub || ""} onChange={(e) => set(ni, { sub: e.target.value })} className={`${ta} mb-2`} placeholder="Sub-line before bullets (optional)" />
            <textarea defaultValue={(c.bullets || []).join("\n")} onBlur={(e) => set(ni, { bullets: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })} rows={2} className={`${ta} mb-2`} placeholder="Bullets — one per line (optional)" />
            <textarea value={c.note || ""} onChange={(e) => set(ni, { note: e.target.value })} rows={2} className={ta} placeholder="Closing note (optional)" />
          </div>
        ))}
        <button onClick={add} className="inline-flex items-center gap-1.5 rounded-full border border-nm-primary/15 px-3.5 py-1.5 text-xs font-medium text-nm-primary hover:bg-nm-primary/5 transition-colors" data-testid={`add-num-${i}`}><Plus size={13} /> Add numbered card</button>
      </div>
    );
  }
  if (blk.t === "table") {
    const headers = blk.headers || [];
    const rows = blk.rows || [];
    return (
      <div className="space-y-2.5">
        <div>
          <label className="text-[11px] font-medium text-nm-neutral">Column headers (one per line)</label>
          <textarea defaultValue={headers.join("\n")} onBlur={(e) => updateBlock(i, { headers: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })} rows={2} className={ta} placeholder={"Decision right\nWhat it means"} data-testid={`table-headers-${i}`} />
        </div>
        <div>
          <label className="text-[11px] font-medium text-nm-neutral">Rows (one per line, columns separated by " | ")</label>
          <textarea defaultValue={rows.map((r) => r.join(" | ")).join("\n")} onBlur={(e) => updateBlock(i, { rows: e.target.value.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => l.split("|").map((c) => c.trim())) })} rows={Math.max(3, rows.length + 1)} className={ta} placeholder={"Decide independently | The employee can proceed without prior approval."} data-testid={`table-rows-${i}`} />
        </div>
      </div>
    );
  }
  if (blk.t === "product") {
    return (
      <div className="space-y-2.5">
        <input value={blk.eyebrow || ""} onChange={(e) => updateBlock(i, { eyebrow: e.target.value })} className={ta} placeholder="Eyebrow tag (e.g. Digital tool)" />
        <div className="flex gap-2">
          <input value={blk.title || ""} onChange={(e) => updateBlock(i, { title: e.target.value })} className={ta} placeholder="Title (e.g. The Team Snapshot)" data-testid={`product-title-${i}`} />
          <input value={blk.price || ""} onChange={(e) => updateBlock(i, { price: e.target.value })} className={`${ta} max-w-[110px]`} placeholder="Price ($39)" />
        </div>
        <textarea value={blk.desc || ""} onChange={(e) => updateBlock(i, { desc: e.target.value })} rows={3} className={ta} placeholder="Description" />
        <textarea defaultValue={(blk.bullets || []).join("\n")} onBlur={(e) => updateBlock(i, { bullets: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })} rows={3} className={ta} placeholder="Feature bullets — one per line" />
        <div className="flex gap-2">
          <input value={blk.cta_label || ""} onChange={(e) => updateBlock(i, { cta_label: e.target.value })} className={`${ta} max-w-[200px]`} placeholder="Button label" />
          <input value={blk.cta_url || ""} onChange={(e) => updateBlock(i, { cta_url: e.target.value })} className={ta} placeholder="https://…" data-testid={`product-url-${i}`} />
        </div>
        <input value={blk.note || ""} onChange={(e) => updateBlock(i, { note: e.target.value })} className={ta} placeholder="Small note beside button (optional)" />
      </div>
    );
  }
  return null;
}
